import { API_BASE_URL } from '../config/api';
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  buildSuggestedQueryPills,
  type AssistantPageKey,
  type AssistantSuggestedQueryPill,
} from '../constants/assistantSuggestedQueries';
import { notifyVoiceSessionChanged } from '../hooks/useDashboardAssistantContext';
import { buildVoiceSessionOrigin } from '../utils/assistantPageContext';
import { useAssistantViewport } from './useAssistantViewport';
import {
  getAssistantDisplayMode,
  isAssistantDesktopViewport,
  subscribeAssistantViewport,
} from '../utils/assistantDisplayMode';
import { log } from '../utils/logger';
import {
  VoiceMessage,
  VoiceSession,
  migrateLegacyHistory,
  getOrCreateCurrentSession,
  getAllSessions,
  getSessionMessages,
  updateSessionMessages,
  startNewSession,
  loadSession,
  clearAllSessions,
} from '../utils/voiceSessionStorage';
import {
  trackAssistantQuestion,
  clearLoopGuard,
  isLoopDetected,
  getLoopRecoveryMessage,
} from '../utils/voiceLoopGuard';
import { speakResponse, stopSpeaking } from '../utils/voiceTts';
import { isVoiceCaptureUiDisabled } from '../voice/killSwitches';
import { getSharedTtsManager } from '../voice/ttsManager';
import { preloadVoiceSettings } from '../utils/voiceSettingsStore';
import {
  buildVoiceCommandRequest,
  createVoiceConfirmCard,
  ensureVoiceSessionId,
  extractVoiceQuickPicks,
  getProactiveMessages,
  getVoiceMessageExtras,
  isFeedbackBlocked,
  isAssistantChatSurfaceActive,
  isIntentFullyExecuted,
  needsContinuation,
  requestServerFastNavigation,
  shouldDeferVoicePageChrome,
  resolveVoiceUiLocale,
  sendVoiceCommand,
  shouldCloseVoiceAssistant,
  storeSharedMind,
  VoiceCommandResult,
  type VoiceQuickPickOption,
} from '../utils/voiceCommandClient';
import {
  clearAllPendingContactFlows,
  clearPendingContactConfirmation,
  storePendingContactConfirmation,
  storePendingContactDisambiguation,
} from '../utils/voicePendingConfirmation';
import {
  clearPendingDocumentFlow,
  storePendingDocumentFlow,
} from '../utils/voicePendingDocumentFlow';
import { resolveVoiceDetailRoute } from '../utils/voiceDetailNavigation';
import {
  checkQuickNavigation,
  executeNavigation,
  getDestinationFromMessage,
  isNavigationActionCommand,
  resolveContactTypeFilter,
  routeFromDestination,
} from '../utils/voiceQuickNav';
import {
  tryClientNavigationFastTrack,
  tryCreateContactFastTrack,
  tryCreateExpenseFastTrack,
  tryCreateInvoiceFastTrack,
  FastTrackResult,
} from '../utils/voiceFastTrack';
import {
  tryDashboardRefreshFastTrack,
  tryDashboardSuggestedQueryFastTrack,
  tryAssistantStarterFastTrack,
  dispatchVoiceDashboardAction,
  isDashboardPath,
  maybeCollapseRailOnVoiceNavigate,
  maybeRefreshDashboardAfterMutation,
} from '../utils/voiceDashboardActions';
import { getDashboardProactiveGreeting, isDashboardProactiveGreeting } from '../utils/voiceModeMessages';
import { isRealMobileDevice } from '../utils/voiceCaptureSupport';
import {
  buildImageProcessingNavigationState,
  isAcceptedUploadFile,
  isSpreadsheetUploadFile,
  resolveBusinessId,
} from '../utils/documentUploadUtils';
import { useHybridAuth } from '../contexts/HybridAuthContext';
import { appAlert } from '../contexts/NotificationModalContext';
import {
  dispatchVoiceDataRefresh,
  shouldDispatchVoiceDataRefresh,
} from '../utils/voiceDataRefresh';
import {
  extractEstablishedIntent,
  navigateForEstablishedIntent,
} from '../utils/voiceIntentNavigation';
import type { ConfirmCardData } from '../components/VoiceConfirmCard';

type Message = VoiceMessage;

const RAIL_STORAGE_KEY = 'ezacc-assistant-rail';

const readStoredRailOpen = (): boolean | null => {
  try {
    const stored = sessionStorage.getItem(RAIL_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    /* ignore storage errors */
  }
  return null;
};

const persistRailOpen = (open: boolean): void => {
  try {
    sessionStorage.setItem(RAIL_STORAGE_KEY, String(open));
  } catch {
    /* ignore storage errors */
  }
};

const appendRouteNavigation = (route: string): void => {
  const currentRoute = `${window.location.pathname}${window.location.search}`;
  if (currentRoute === route) return;
  window.dispatchEvent(new CustomEvent('voiceNavigation', { detail: { route } }));
};

export const useGlobalVoiceAssistant = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { business } = useHybridAuth();
  const { t } = useTranslation();
  const isDesktop = useAssistantViewport();
  const [railOpen, setRailOpenState] = useState(() => {
    const stored = readStoredRailOpen();
    if (stored !== null && !isAssistantDesktopViewport()) return stored;
    return isAssistantDesktopViewport() || isDashboardPath(window.location.pathname);
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<VoiceMessage[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [pastSessions, setPastSessions] = useState<VoiceSession[]>([]);
  const [selectedPastSession, setSelectedPastSession] = useState<VoiceSession | null>(null);
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const [componentData, setComponentData] = useState<any>(null);
  const [confirmCard, setConfirmCard] = useState<ConfirmCardData | null>(null);
  const [quickPicks, setQuickPicks] = useState<VoiceQuickPickOption[]>([]);
  const [dynamicSuggestedQueries, setDynamicSuggestedQueries] = useState<string[]>([]);
  const sessionIdRef = useRef<string>('');
  const pendingDataRefreshRef = useRef<VoiceCommandResult | null>(null);
  const activeIntentRef = useRef<string | null>(null);
  const navigatedForIntentRef = useRef<string | null>(null);
  const dashboardGreetingInjectedRef = useRef(false);

  const pageSuggestedQueries = useMemo(
    () => buildSuggestedQueryPills(
      buildVoiceSessionOrigin(location.pathname, location.search).pageKey as AssistantPageKey,
      t,
      dynamicSuggestedQueries
    ),
    [dynamicSuggestedQueries, location.pathname, location.search, t]
  );

  const sessionOrigin = useCallback(
    () => buildVoiceSessionOrigin(location.pathname, location.search),
    [location.pathname, location.search]
  );

  useEffect(() => {
    migrateLegacyHistory();
    const session = getOrCreateCurrentSession(sessionOrigin());
    sessionIdRef.current = session.id;
    setMessages(session.messages);
    setPastSessions(getAllSessions());
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    setRailOpenState(true);
    persistRailOpen(true);
  }, [isDesktop]);

  useEffect(() => {
    if (isDesktop || readStoredRailOpen() !== null) return;
    setRailOpenState(isDashboardPath(location.pathname));
  }, [isDesktop, location.pathname]);

  useEffect(() => {
    return subscribeAssistantViewport((desktop) => {
      if (desktop) {
        setIsOpen(false);
        setIsMinimized(false);
      }
    });
  }, []);

  const setRailOpen = useCallback((open: boolean) => {
    setRailOpenState(open);
    persistRailOpen(open);
  }, []);

  const toggleRail = useCallback(() => {
    setRailOpenState((prev) => {
      const next = !prev;
      persistRailOpen(next);
      return next;
    });
  }, []);

  const displayMode = getAssistantDisplayMode({ isDesktop, railOpen, isOpen });

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      preloadVoiceSettings().catch(() => undefined);
    }
  }, []);

  const generateMessageId = useCallback(() => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`, []);

  const persistMessages = useCallback((msgs: VoiceMessage[]) => {
    if (!sessionIdRef.current) return;
    updateSessionMessages(sessionIdRef.current, msgs);
    setPastSessions(getAllSessions());
  }, []);

  const toVoiceMessage = useCallback((role: 'user' | 'assistant', content: string, extra?: Partial<VoiceMessage>): VoiceMessage => ({
    id: generateMessageId(),
    role,
    content,
    timestamp: new Date().toISOString(),
    ...extra,
  }), [generateMessageId]);

  const appendAssistantMessage = useCallback((content: string, extra?: Partial<VoiceMessage>) => {
    const message = toVoiceMessage('assistant', content, extra);
    setMessages(prev => [...prev, message]);
    return message;
  }, [toVoiceMessage]);

  useEffect(() => {
    if (messages.length === 0 || !sessionIdRef.current) return;
    const timer = setTimeout(() => persistMessages(messages), 300);
    return () => clearTimeout(timer);
  }, [messages, persistMessages]);

  const pendingAutoStartRef = useRef(false);

  const startVoiceInput = useCallback((prewarmedStream?: MediaStream | null) => {
    if (isVoiceCaptureUiDisabled()) {
      log.voice.start();
      return;
    }
    log.voice.start();
    setIsProcessing(false);
    setCurrentTranscript('');
    // Dispatch immediately so getUserMedia stays inside the user-gesture window
    // on iOS. TTS prep runs in parallel and must not delay capture start.
    window.dispatchEvent(new CustomEvent('startVoiceInput', {
      detail: { stream: prewarmedStream ?? null },
    }));
    void getSharedTtsManager().prepareForCapture().catch(() => undefined);
  }, []);

  const stopVoiceInput = useCallback((mode: 'done' | 'cancel' = 'done') => {
    window.dispatchEvent(new CustomEvent('stopVoiceInput', {
      detail:
        mode === 'cancel'
          ? { disposition: 'discard', reason: 'user-cancel' }
          : { disposition: 'submit', reason: 'user-done' },
    }));
    setIsRecording(false);
    setCurrentTranscript('');
  }, []);

  useEffect(() => {
    if (!isOpen || isMinimized || isProcessing || !pendingAutoStartRef.current) return;

    pendingAutoStartRef.current = false;
    const timer = window.setTimeout(() => {
      startVoiceInput();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [isOpen, isMinimized, isProcessing, startVoiceInput]);

  const beginNewChatSession = useCallback(() => {
    if (messages.length > 0) persistMessages(messages);
    const session = startNewSession(sessionOrigin());
    sessionIdRef.current = session.id;
    notifyVoiceSessionChanged();
    setMessages([]);
    setCurrentTranscript('');
    setShowHistoryModal(false);
    setConfirmCard(null);
    setDynamicSuggestedQueries([]);
    clearLoopGuard();
    clearPendingContactConfirmation();
    clearPendingDocumentFlow();
    activeIntentRef.current = null;
    navigatedForIntentRef.current = null;
    localStorage.removeItem('voiceSessionId');
    const gvaState: any = (window as any).__GVA_STATE__;
    if (gvaState) {
      delete gvaState.sharedMind;
      delete gvaState.selectedItems;
      delete gvaState.filters;
    }
    setPastSessions(getAllSessions());
  }, [messages, persistMessages, sessionOrigin]);

  const openAssistant = useCallback(() => {
    beginNewChatSession();

    const desktop = isAssistantDesktopViewport();
    if (desktop) {
      setRailOpen(true);
      setIsMinimized(false);
      return;
    }

    setIsOpen(true);
    setIsMinimized(false);
    // Mobile browsers require a direct tap on the mic button to unlock the microphone.
    pendingAutoStartRef.current = !isRealMobileDevice();
  }, [beginNewChatSession, setRailOpen]);

  const closeAssistant = useCallback(() => {
    if (messages.length > 0) persistMessages(messages);
    stopSpeaking();
    window.dispatchEvent(new CustomEvent('stopVoiceInput'));
    if (pendingDataRefreshRef.current) {
      dispatchVoiceDataRefresh(pendingDataRefreshRef.current);
      pendingDataRefreshRef.current = null;
    }
    setIsOpen(false);
    setIsMinimized(false);
    setIsRecording(false);
    setCurrentTranscript('');
    setConfirmCard(null);
    setQuickPicks([]);
    setDynamicSuggestedQueries([]);
    activeIntentRef.current = null;
    navigatedForIntentRef.current = null;
    pendingAutoStartRef.current = false;
  }, [messages, persistMessages]);

  const minimizeAssistant = useCallback(() => {
    window.dispatchEvent(new CustomEvent('stopVoiceInput'));
    setIsRecording(false);
    setIsMinimized(true);
  }, []);

  const maximizeAssistant = useCallback(() => {
    setIsMinimized(false);
    pendingAutoStartRef.current = !isRealMobileDevice();
  }, []);

  const dismissAssistantAfterNavigation = useCallback(() => {
    setConfirmCard(null);
    if (!isDesktop) {
      setMessages([]);
      closeAssistant();
      return;
    }
    setTimeout(() => closeAssistant(), 400);
  }, [closeAssistant, isDesktop]);

  const addFastTrackMessage = useCallback((result: FastTrackResult) => {
    if (result.message) appendAssistantMessage(result.message);
    if (result.navigationHref) {
      appendRouteNavigation(result.navigationHref);
      dismissAssistantAfterNavigation();
    }
    if (result.action) {
      const mockResult: VoiceCommandResult = {
        success: true,
        action: result.action,
        message: result.message
      };
      if (shouldDispatchVoiceDataRefresh(mockResult)) {
        pendingDataRefreshRef.current = mockResult;
        dispatchVoiceDataRefresh(mockResult);
        maybeRefreshDashboardAfterMutation(mockResult.action);
      }
    }
  }, [appendAssistantMessage, dismissAssistantAfterNavigation]);

  const tryClientFastTracks = useCallback(async (text: string): Promise<boolean> => {
    const directNav = tryClientNavigationFastTrack(text);
    if (directNav) {
      addFastTrackMessage(directNav);
      return true;
    }

    for (const creator of [tryCreateExpenseFastTrack, tryCreateInvoiceFastTrack, tryCreateContactFastTrack]) {
      const result = await creator(text);
      if (!result) continue;
      addFastTrackMessage(result);
      if (result.handled) return true;
    }

    return false;
  }, [addFastTrackMessage]);

  const handleLocalNavigation = useCallback((text: string): boolean => {
    if (isNavigationActionCommand(text)) return false;
    const navigationResult = checkQuickNavigation(text);
    if (!navigationResult) return false;

    maybeCollapseRailOnVoiceNavigate(navigationResult.destination, setRailOpen);
    executeNavigation(navigationResult.destination);
    const contactType = resolveContactTypeFilter(text);
    if (contactType) {
      window.dispatchEvent(new CustomEvent('voiceContactsAction', { detail: { filterType: contactType } }));
    }
    appendAssistantMessage(navigationResult.response);
    dismissAssistantAfterNavigation();
    return true;
  }, [appendAssistantMessage, dismissAssistantAfterNavigation, setRailOpen]);

  const handleServerFastNavigation = useCallback(async (text: string): Promise<boolean> => {
    try {
      const result = await requestServerFastNavigation(text);
      if (!result) return false;
      maybeCollapseRailOnVoiceNavigate(result.data.destination, setRailOpen);
      executeNavigation(result.data.destination);
      appendAssistantMessage(result.message);
      dismissAssistantAfterNavigation();
      return true;
    } catch (error) {
      log.debug?.('Fast navigation server call failed, falling back to full processing:', error);
      return false;
    }
  }, [appendAssistantMessage, dismissAssistantAfterNavigation, setRailOpen]);

  const scheduleProactiveMessages = useCallback((result: VoiceCommandResult) => {
    for (const proactive of getProactiveMessages(result)) {
      setTimeout(() => appendAssistantMessage(proactive.content), proactive.delay);
    }
  }, [appendAssistantMessage]);

  const updateConfirmCard = useCallback((result: VoiceCommandResult) => {
    setConfirmCard(createVoiceConfirmCard(
      result,
      () => {
        setConfirmCard(null);
        window.dispatchEvent(new CustomEvent('voiceConfirmAction', { detail: { text: 'yes confirm' } }));
      },
      () => {
        setConfirmCard(null);
        window.dispatchEvent(new CustomEvent('voiceConfirmAction', { detail: { text: 'cancel' } }));
      },
      { isMobile: !isDesktop }
    ));
  }, [isDesktop]);

  const handleResultNavigation = useCallback((result: VoiceCommandResult): boolean => {
    const assistantChatActive = isAssistantChatSurfaceActive(isDesktop, railOpen, isOpen);
    const stayInChat = assistantChatActive && shouldDeferVoicePageChrome(result);

    const dispatchPageAction = (eventName: string, detail: Record<string, unknown>) => {
      if (stayInChat) return;
      window.dispatchEvent(new CustomEvent(eventName, { detail }));
    };

    if (result.data?.expensesPageAction) {
      dispatchPageAction('voiceExpensesAction', result.data.expensesPageAction);
    }

    if (result.data?.contactsPageAction) {
      const contactsAction = { ...result.data.contactsPageAction };
      if (stayInChat) {
        delete contactsAction.openCreate;
      }
      dispatchPageAction('voiceContactsAction', contactsAction);
    }

    if (result.data?.chartOfAccountsPageAction) {
      dispatchPageAction('voiceChartOfAccountsAction', result.data.chartOfAccountsPageAction);
    }

    if (result.data?.bankAccountsPageAction) {
      dispatchPageAction('voiceBankAccountsAction', result.data.bankAccountsPageAction);
    }

    if (result.data?.invoicesPageAction) {
      dispatchPageAction('voiceInvoicesAction', result.data.invoicesPageAction);
    }

    if (result.data?.quotesPageAction) {
      dispatchPageAction('voiceQuotesAction', result.data.quotesPageAction);
    }

    if (result.data?.inventoryPageAction) {
      dispatchPageAction('voiceInventoryAction', result.data.inventoryPageAction);
    }

    if (result.data?.taxPeriodsPageAction) {
      dispatchPageAction('voiceTaxPeriodsAction', result.data.taxPeriodsPageAction);
    }

    if (result.data?.reportsPageAction) {
      dispatchPageAction('voiceReportsAction', result.data.reportsPageAction);
    }

    if (result.data?.bulkPaymentsPageAction) {
      dispatchPageAction('voiceBulkPaymentsPageAction', result.data.bulkPaymentsPageAction);
    }

    if (result.data?.imageProcessingPageAction) {
      dispatchPageAction('voiceImageProcessingPageAction', result.data.imageProcessingPageAction);
    }

    if (result.data?.dashboardPageAction && !stayInChat) {
      dispatchVoiceDashboardAction(result.data.dashboardPageAction);
    }

    const detailRoute = stayInChat ? null : resolveVoiceDetailRoute(result);
    if (detailRoute) {
      maybeCollapseRailOnVoiceNavigate(detailRoute, setRailOpen);
      appendRouteNavigation(detailRoute);
      dismissAssistantAfterNavigation();
      return true;
    }

    const isClarifying = shouldDeferVoicePageChrome(result);

    if (!isClarifying && !stayInChat) {
      const destinationFromMessage = getDestinationFromMessage(result.message);
      if (destinationFromMessage) {
        maybeCollapseRailOnVoiceNavigate(destinationFromMessage, setRailOpen);
        executeNavigation(destinationFromMessage);
        dismissAssistantAfterNavigation();
        return true;
      }
    }

    if (!stayInChat && result.action === 'navigate' && result.data?.route) {
      maybeCollapseRailOnVoiceNavigate(result.data.route, setRailOpen);
      appendRouteNavigation(result.data.route);
      dismissAssistantAfterNavigation();
      return true;
    }

    if (!stayInChat && result.action === 'navigate' && result.data?.destination) {
      const route = routeFromDestination(result.data.destination);
      maybeCollapseRailOnVoiceNavigate(route, setRailOpen);
      appendRouteNavigation(route);
      dismissAssistantAfterNavigation();
      return true;
    }

    return false;
  }, [dismissAssistantAfterNavigation, isDesktop, isOpen, railOpen, setRailOpen]);

  const handleVoiceCommandResult = useCallback((result: VoiceCommandResult, sessionId: string) => {
    storeSharedMind(result);
    scheduleProactiveMessages(result);

    const proactiveSuggestions = result.shared_mind?.proactive_suggestions;
    if (Array.isArray(proactiveSuggestions) && proactiveSuggestions.length > 0) {
      const queries = proactiveSuggestions
        .map((item: { text?: string }) => item?.text?.trim())
        .filter((text): text is string => Boolean(text))
        .slice(0, 4);
      setDynamicSuggestedQueries(queries);
    } else if (Array.isArray(result.suggestions) && result.suggestions.length > 0) {
      const queries = result.suggestions
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean)
        .slice(0, 4);
      setDynamicSuggestedQueries(queries);
    }

    if (result.session_id) {
      localStorage.setItem('voiceSessionId', result.session_id);
    } else {
      localStorage.setItem('voiceSessionId', sessionId);
    }

    if (result.phase === 'contact_confirmation') {
      storePendingContactConfirmation(result);
    } else if (result.phase === 'disambiguation') {
      storePendingContactDisambiguation(result);
    } else {
      clearAllPendingContactFlows();
    }

    if (
      result.phase === 'gathering_customer'
      || result.data?.phase === 'gathering_customer'
      || result.phase === 'gathering_items'
      || result.data?.gathering_items === true
      || result.data?.missing_entity?.startsWith('item_')
      || result.data?.missing_entity === 'more_items'
    ) {
      storePendingDocumentFlow(result);
    } else if (shouldCloseVoiceAssistant(result) || result.phase === 'cancelled') {
      clearPendingDocumentFlow();
    }

    if (isFeedbackBlocked(result)) return;

    const spokenMessage = result.message || (result.success ? 'Done.' : 'Sorry, I could not complete that. Please try again.');
    const displayMessage = result.data?.full_message || spokenMessage;

    const assistantMessage = appendAssistantMessage(
      displayMessage,
      getVoiceMessageExtras(result)
    );
    speakResponse(spokenMessage);

    if (
      result.phase === 'gathering_data'
      || result.phase === 'gathering_quote'
      || result.phase === 'gathering_items'
      || result.data?.phase === 'gathering_quote'
      || result.data?.gathering_items === true
      || result.needsConfirmation
      || result.requires_confirmation
      || result.phase === 'confirmation'
    ) {
      trackAssistantQuestion(
        result.message || result.nextQuestion || result.missingFields?.[0] || '',
        result.nextQuestion || result.missingFields?.[0]
      );
    }

    if (
      result.phase === 'gathering_customer' ||
      result.data?.phase === 'gathering_customer' ||
      result.phase === 'gathering_quote' ||
      result.data?.phase === 'gathering_quote' ||
      (result.data?.suggested_customers && Array.isArray(result.data.suggested_customers)) ||
      (result.data?.suggested_suppliers && Array.isArray(result.data.suggested_suppliers))
    ) {
      setQuickPicks(extractVoiceQuickPicks(result));
    } else {
      setQuickPicks([]);
    }

    updateConfirmCard(result);

    const navigated = handleResultNavigation(result);
    if (navigated) {
      setConfirmCard(null);
    }

    const establishedIntent = extractEstablishedIntent(result);
    const assistantChatActive = isAssistantChatSurfaceActive(isDesktop, railOpen, isOpen);
    const stayInChat = assistantChatActive && shouldDeferVoicePageChrome(result);
    if (!navigated && establishedIntent && !stayInChat) {
      if (activeIntentRef.current !== establishedIntent) {
        navigatedForIntentRef.current = null;
        activeIntentRef.current = establishedIntent;
      }
      if (navigatedForIntentRef.current !== establishedIntent) {
        if (navigateForEstablishedIntent(establishedIntent)) {
          navigatedForIntentRef.current = establishedIntent;
        }
      }
    }

    if (shouldCloseVoiceAssistant(result)) {
      setQuickPicks([]);
      setTimeout(() => closeAssistant(), 1500);
    } else if (!navigated && result.action !== 'navigate') {
      if (isIntentFullyExecuted(result) && !needsContinuation(result)) {
        clearLoopGuard();
      } else if (needsContinuation(result)) {
        setIsOpen(true);
      }
    }

    if (result.data?.component) {
      setActiveComponent(result.data.component);
      setComponentData(result.data.componentData);
    }

    if (shouldDispatchVoiceDataRefresh(result)) {
      pendingDataRefreshRef.current = result;
      dispatchVoiceDataRefresh(result);
      maybeRefreshDashboardAfterMutation(result.action);
    }
  }, [appendAssistantMessage, closeAssistant, handleResultNavigation, isDesktop, isOpen, railOpen, scheduleProactiveMessages, updateConfirmCard]);

  useEffect(() => {
    dashboardGreetingInjectedRef.current = false;

    setMessages((prev) => {
      const next = prev.filter(
        (msg) => !(msg.role === 'assistant' && isDashboardProactiveGreeting(msg.content))
      );
      if (next.length !== prev.length) {
        persistMessages(next);
        return next;
      }
      return prev;
    });
  }, [location.pathname, persistMessages]);

  useEffect(() => {
    if (!isDashboardPath(location.pathname)) {
      return;
    }

    const assistantVisible = isDesktop ? railOpen : isOpen;
    // Desktop rail uses VoiceAssistantRailEmptyState for welcome content.
    if (isDesktop && railOpen) return;
    if (!assistantVisible || messages.length > 0 || dashboardGreetingInjectedRef.current) return;

    dashboardGreetingInjectedRef.current = true;
    appendAssistantMessage(getDashboardProactiveGreeting(i18n.language));
  }, [appendAssistantMessage, i18n.language, isDesktop, isOpen, location.pathname, messages.length, railOpen]);

  const handleTranscription = useCallback(async (text: string) => {
    const trimmed = text.trim();
    const isNumericQuickPick = /^\d+$/.test(trimmed);
    if (!trimmed || (trimmed.length < 2 && !isNumericQuickPick)) {
      setIsProcessing(false);
      return;
    }

    log.voice.transcription(trimmed);

    if (isLoopDetected()) {
      appendAssistantMessage(getLoopRecoveryMessage());
      clearLoopGuard();
      setIsProcessing(false);
      return;
    }

    setMessages(prev => [...prev, toVoiceMessage('user', trimmed)]);
    setIsProcessing(true);
    setCurrentTranscript('');

    try {
      const dashboardRefresh = tryDashboardRefreshFastTrack(trimmed);
      if (dashboardRefresh) {
        appendAssistantMessage(dashboardRefresh.message);
        return;
      }

      const suggestedQuery = tryDashboardSuggestedQueryFastTrack(trimmed, setRailOpen);
      if (suggestedQuery) {
        appendAssistantMessage(suggestedQuery.message);
        return;
      }
      const assistantChatActive = isAssistantChatSurfaceActive(isDesktop, railOpen, isOpen);
      const starterAction = tryAssistantStarterFastTrack(trimmed, setRailOpen, { assistantChatActive });
      if (starterAction) {
        appendAssistantMessage(starterAction.message);
        return;
      }
      if (await tryClientFastTracks(trimmed)) return;
      if (handleLocalNavigation(trimmed)) return;
      if (await handleServerFastNavigation(trimmed)) return;

      const sessionId = ensureVoiceSessionId();
      const result = await sendVoiceCommand(trimmed, sessionId, {
        isOpen,
        isMinimized,
        isRecording,
        messages,
        assistantContext: (window as any).__GVA_STATE__?.assistantContext,
      });
      log.voice.response(result);
      handleVoiceCommandResult(result, sessionId);
    } catch (error) {
      log.error?.('Voice command error:', error);
      appendAssistantMessage(error instanceof Error ? error.message : 'Sorry, I encountered an error processing your request.');
    } finally {
      setIsProcessing(false);
      setIsRecording(false);
      setCurrentTranscript('');
    }
  }, [appendAssistantMessage, handleLocalNavigation, handleServerFastNavigation, handleVoiceCommandResult, isDesktop, isMinimized, isOpen, isRecording, messages, railOpen, setRailOpen, toVoiceMessage, tryClientFastTracks]);

  const handleProcessingChange = useCallback((processing: boolean) => setIsProcessing(processing), []);
  const handleOpenHistory = useCallback((show?: boolean) => {
    setPastSessions(getAllSessions());
    setShowHistoryModal(show !== undefined ? show : true);
  }, []);
  const handleCloseHistoryModal = useCallback(() => {
    setShowHistoryModal(false);
    setSelectedPastSession(null);
  }, []);
  const handleSelectPastSession = useCallback((session: VoiceSession) => setSelectedPastSession(session), []);

  const handleLoadSession = useCallback((session: VoiceSession) => {
    if (!session?.id) return;
    const loaded = loadSession(session.id);
    if (!loaded) return;
    sessionIdRef.current = loaded.id;
    setMessages(getSessionMessages(loaded.id));
    setSelectedPastSession(loaded);
    setShowHistoryModal(false);
    clearLoopGuard();
  }, []);

  const handleLoadSelectedSession = useCallback(() => {
    if (!selectedPastSession) return;
    handleLoadSession(selectedPastSession);
  }, [handleLoadSession, selectedPastSession]);

  /**
   * Revert the chat to a past message: everything after that message is
   * discarded, along with any pending confirmation / server dialog state that
   * later turns created. When `sessionId` points to another session, that
   * session is loaded first ("go here" from the history preview).
   */
  const handleRewindToMessage = useCallback((messageId: string, sessionId?: string) => {
    if (isProcessing || isRecording) return;

    let source = messages;
    if (sessionId && sessionId !== sessionIdRef.current) {
      const loaded = loadSession(sessionId);
      if (!loaded) return;
      sessionIdRef.current = loaded.id;
      source = getSessionMessages(loaded.id);
    }

    const idx = source.findIndex((m) => m.id === messageId);
    if (idx === -1) return;

    const trimmed = source.slice(0, idx + 1);
    stopSpeaking();
    setMessages(trimmed);
    if (sessionIdRef.current) {
      updateSessionMessages(sessionIdRef.current, trimmed);
      setPastSessions(getAllSessions());
    }
    setShowHistoryModal(false);
    setSelectedPastSession(null);

    // Discarded turns may have pending confirmations or server dialog state —
    // clear them so the next command starts clean from the rewound point.
    setCurrentTranscript('');
    setConfirmCard(null);
    setQuickPicks([]);
    setDynamicSuggestedQueries([]);
    clearLoopGuard();
    clearPendingContactConfirmation();
    clearPendingDocumentFlow();
    activeIntentRef.current = null;
    navigatedForIntentRef.current = null;
    localStorage.removeItem('voiceSessionId');
    const gvaState: any = (window as any).__GVA_STATE__;
    if (gvaState) {
      delete gvaState.sharedMind;
      delete gvaState.selectedItems;
      delete gvaState.filters;
    }
  }, [isProcessing, isRecording, messages]);

  const handleStartNewChat = useCallback(() => {
    beginNewChatSession();
  }, [beginNewChatSession]);

  const handleClearHistory = useCallback(() => {
    clearAllSessions();
    const session = startNewSession(sessionOrigin());
    sessionIdRef.current = session.id;
    notifyVoiceSessionChanged();
    setPastSessions([]);
    setMessages([]);
    setSelectedPastSession(null);
    localStorage.removeItem('voiceSessionId');
    clearPendingContactConfirmation();
    clearPendingDocumentFlow();
  }, [sessionOrigin]);

  const handleCloseComponent = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const handleConfirmTransaction = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const handleConfirmQuotation = useCallback(() => {
    setActiveComponent(null);
    setComponentData(null);
  }, []);

  const setupTranscriptListener = useCallback(() => {
    const handler = (event: CustomEvent) => setCurrentTranscript(event.detail.transcript);
    window.addEventListener('transcriptUpdate', handler as EventListener);
    return () => window.removeEventListener('transcriptUpdate', handler as EventListener);
  }, []);

  const setupRecordingListener = useCallback(() => {
    const handler = (event: CustomEvent) => setIsRecording(event.detail);
    window.addEventListener('recordingStateChange', handler as EventListener);
    return () => window.removeEventListener('recordingStateChange', handler as EventListener);
  }, []);

  const processSpreadsheetUpload = useCallback(async (file: File) => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('locale', resolveVoiceUiLocale());

      const response = await fetch(`${API_BASE_URL}/voice/process-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      appendAssistantMessage(result.message || `Successfully processed ${result.data?.processed || 0} records from ${file.name}`);
      if (result.data?.summary) appendAssistantMessage(`Summary: ${result.data.summary}`);
      speakResponse(result.message || `Processed ${result.data?.processed || 0} records.`);
    } catch (error) {
      appendAssistantMessage(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
    }
  }, [appendAssistantMessage]);

  const handleFilesUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const label = files.length === 1 ? files[0].name : `${files.length} files`;
    setMessages((prev) => [...prev, toVoiceMessage('user', `Uploaded: ${label}`)]);

    const documentFiles = files.filter(isAcceptedUploadFile);
    if (documentFiles.length > 0) {
      const businessId = resolveBusinessId(business);
      if (!businessId) {
        void appAlert('Please select a business before scanning a document.', { type: 'error' });
        return;
      }
      const navigationState = buildImageProcessingNavigationState(documentFiles);
      if (!navigationState) {
        appendAssistantMessage('Please choose a photo, PDF, or spreadsheet file.');
        return;
      }
      appendAssistantMessage('Opening document scan…');
      navigate('/image-processing', { state: navigationState });
    }

    const spreadsheetFiles = files.filter(
      (file) => !isAcceptedUploadFile(file) && isSpreadsheetUploadFile(file)
    );
    for (const file of spreadsheetFiles) {
      await processSpreadsheetUpload(file);
    }

    const unsupported = files.filter(
      (file) => !isAcceptedUploadFile(file) && !isSpreadsheetUploadFile(file)
    );
    if (unsupported.length > 0 && documentFiles.length === 0 && spreadsheetFiles.length === 0) {
      appendAssistantMessage(
        'Unsupported file type. Attach a receipt or invoice photo (JPG, PNG, PDF) or a CSV/Excel import file.'
      );
    }
  }, [appendAssistantMessage, business, navigate, processSpreadsheetUpload, toVoiceMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const fromList = event.detail?.files as File[] | undefined;
      const single = event.detail?.file as File | undefined;
      const files = fromList?.length ? fromList : single ? [single] : [];
      if (files.length) void handleFilesUpload(files);
    };
    window.addEventListener('voiceAssistantFileUpload', handler as EventListener);
    return () => window.removeEventListener('voiceAssistantFileUpload', handler as EventListener);
  }, [handleFilesUpload]);

  useEffect(() => {
    const handler = () => {
      appendAssistantMessage(i18n.t('dashboard.assistant.dashboardUpdated', 'Dashboard updated'));
    };

    window.addEventListener('voiceDashboardRefreshComplete', handler as EventListener);
    return () => window.removeEventListener('voiceDashboardRefreshComplete', handler as EventListener);
  }, [appendAssistantMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const message = event.detail?.message;
      if (message) {
        appendAssistantMessage(message, { isError: true });
      }
    };
    window.addEventListener('voiceMicError', handler as EventListener);
    return () => window.removeEventListener('voiceMicError', handler as EventListener);
  }, [appendAssistantMessage]);

  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const text = event.detail?.text;
      if (text) handleTranscription(text);
    };
    window.addEventListener('voiceConfirmAction', handler as EventListener);
    return () => window.removeEventListener('voiceConfirmAction', handler as EventListener);
  }, [handleTranscription]);

  useEffect(() => {
    const handler = (event: Event) => {
      const show = (event as CustomEvent<{ show?: boolean }>).detail?.show;
      handleOpenHistory(show !== false);
    };
    window.addEventListener('voiceOpenHistory', handler as EventListener);
    return () => window.removeEventListener('voiceOpenHistory', handler as EventListener);
  }, [handleOpenHistory]);

  const handleQuickPickSelect = useCallback((option: VoiceQuickPickOption) => {
    const text = option.quote_number || option.name || String(option.index);
    void handleTranscription(text);
  }, [handleTranscription]);

  return {
    isOpen,
    isMinimized,
    railOpen,
    displayMode,
    toggleRail,
    messages,
    isRecording,
    isProcessing,
    currentTranscript,
    showHistoryModal,
    pastSessions,
    selectedPastSession,
    activeComponent,
    componentData,
    confirmCard,
    quickPicks,
    dynamicSuggestedQueries,
    pageSuggestedQueries,
    handleQuickPickSelect,
    openAssistant,
    closeAssistant,
    minimizeAssistant,
    maximizeAssistant,
    startVoiceInput,
    stopVoiceInput,
    handleTranscription,
    handleProcessingChange,
    handleOpenHistory,
    handleCloseHistoryModal,
    handleSelectPastSession,
    handleLoadSession,
    handleLoadSelectedSession,
    handleRewindToMessage,
    handleStartNewChat,
    handleClearHistory,
    handleCloseComponent,
    handleConfirmTransaction,
    handleConfirmQuotation,
    setupTranscriptListener,
    setupRecordingListener
  };
};
